/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83): the subtitle of the call header in the final design —
 * "4:12" in a call of two, "6 participants · 18:04" in a group call, with
 * a lock when the call is end-to-end encrypted, and the quality of our
 * connection when it is bad (a good one is shown by IonAudioCallView in an
 * audio call, and not at all in the final design of a video call).
 */

import { type FC, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LockSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import { pick } from "./callErrorTexts";
import {
  CALL_SCREEN_TEXTS as T,
  durationText,
  participantsText,
} from "./callScreen";
import { type IonConnectionQuality } from "./connectionQuality";
import styles from "./IonCallSubtitle.module.css";

export const QUALITY_TEXT = {
  good: T.goodConnection,
  poor: T.poorConnection,
  lost: T.lostConnection,
} as const;

/** Three bars, as many filled as the quality is good. */
export const QualityBars: FC<{
  quality: Exclude<IonConnectionQuality, null>;
}> = ({ quality }) => {
  const filled = quality === "good" ? 3 : quality === "poor" ? 2 : 0;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={1 + i * 4.5}
          y={9 - i * 3.5}
          width="3"
          height={4 + i * 3.5}
          rx="1"
          fill="currentColor"
          opacity={i < filled ? 1 : 0.3}
        />
      ))}
    </svg>
  );
};

interface Props {
  /** When the call view started, in ms. */
  startedAt: number;
  participants: number;
  encrypted: boolean;
  quality?: IonConnectionQuality;
  /** For tests. */
  now?: () => number;
}

/** The lock of an end-to-end encrypted call. */
export const IonEncryptedLock: FC = () => {
  const { i18n } = useTranslation();
  return (
    <LockSolidIcon
      className={styles.lock}
      width={14}
      height={14}
      aria-label={pick(T.encrypted, i18n.language)}
    />
  );
};

/** Element Call's "Calling…" with the lock (Figma 08·03). */
export const IonRingingSubtitle: FC<{
  encrypted: boolean;
  children: ReactNode;
}> = ({ encrypted, children }) => (
  <span className={styles.subtitle}>
    {encrypted && <IonEncryptedLock />}
    {children}
  </span>
);

export const IonCallSubtitle: FC<Props> = ({
  startedAt,
  participants,
  encrypted,
  quality = null,
  now = Date.now,
}) => {
  const { i18n } = useTranslation();
  const [time, setTime] = useState(now);
  useEffect(() => {
    const interval = setInterval(() => setTime(now()), 1000);
    return (): void => clearInterval(interval);
  }, [now]);

  const duration = durationText((time - startedAt) / 1000);
  const text =
    participants > 2
      ? `${participantsText(participants, i18n.language)} · ${duration}`
      : duration;
  return (
    <span className={styles.subtitle}>
      {encrypted && <IonEncryptedLock />}
      <span data-testid="ion-call-duration">{text}</span>
      {quality !== null && quality !== "good" && (
        <span
          className={styles.quality}
          data-quality={quality}
          data-testid="ion-call-quality"
        >
          <QualityBars quality={quality} />
          {pick(QUALITY_TEXT[quality], i18n.language)}
        </span>
      )}
    </span>
  );
};
